// Warehouses and projects (CR_Manager_vs_User_role P2): two views over the same sites. A yard or warehouse is the
// company's own place; a project or customer site is a location of a project. Web and phone group places the same
// way through these helpers.

export type SiteType = 'yard' | 'warehouse' | 'project_site' | 'customer_site';
export type PlaceKind = 'warehouse' | 'location';

export const WAREHOUSE_TYPES: readonly SiteType[] = ['yard', 'warehouse'];
export const LOCATION_TYPES: readonly SiteType[] = ['project_site', 'customer_site'];

export function placeKind(type: SiteType): PlaceKind {
  return WAREHOUSE_TYPES.includes(type) ? 'warehouse' : 'location';
}

export interface PlaceLike {
  site_id: string;
  name: string;
  type: SiteType;
  project_id: string | null;
  is_active: boolean;
}

export interface ProjectLike {
  id: string;
  name: string;
}

export interface PlaceGroups<P extends PlaceLike, J extends ProjectLike> {
  warehouses: P[];
  projects: { project: J; locations: P[] }[];
  /** project locations that belong to no project (or to one the reader can't see) */
  other: P[];
}

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);

/** Active places grouped Warehouses · Projects · Other locations, each sorted by name. */
export function groupPlaces<P extends PlaceLike, J extends ProjectLike>(
  places: readonly P[],
  projects: readonly J[],
): PlaceGroups<P, J> {
  const active = places.filter((p) => p.is_active);
  const known = new Map(projects.map((p) => [p.id, p]));
  const byProject = new Map<string, P[]>();
  const other: P[] = [];
  for (const p of active) {
    if (placeKind(p.type) === 'warehouse') continue;
    const project = p.project_id ? known.get(p.project_id) : undefined;
    if (!project) {
      other.push(p);
      continue;
    }
    byProject.set(project.id, [...(byProject.get(project.id) ?? []), p]);
  }
  return {
    warehouses: active.filter((p) => placeKind(p.type) === 'warehouse').sort(byName),
    projects: [...byProject]
      .map(([id, locations]) => ({ project: known.get(id)!, locations: locations.sort(byName) }))
      .sort((a, b) => byName(a.project, b.project)),
    other: other.sort(byName),
  };
}

/**
 * What a transfer end reads when there is no name to show (owner, 2026-09-27) — three different cases that all used
 * to print "—": nothing on the `from` side (the engine opened it for an asset that had no custody), nothing on the
 * `to` side (a draft whose destination is not chosen yet), or a place the viewer may not see by name.
 */
export type TransferEndKey =
  'transfers.end_no_previous' | 'transfers.end_not_chosen' | 'transfers.end_other_place';
export function transferEndKey(id: string | null | undefined, side: 'from' | 'to'): TransferEndKey {
  if (id) return 'transfers.end_other_place';
  return side === 'from' ? 'transfers.end_no_previous' : 'transfers.end_not_chosen';
}
